import { computed, ref } from 'vue'
import { useNow } from '@/composables/game/time'
import { usePlayer, usePlayerProfit } from '@/composables/game/player'
import { useConfig } from '@/composables/game/config'
import { getNextOccurrence, getNextOccurrenceDiffFromNow, isTodayDate, thisOccurrence } from '@/helpers/date'
import { DateTime } from 'luxon'
import api from '@/services/api'
import { DAILY_TASK_ID } from '@/types/shared/player'
import { useAd } from '@/composables/game/ad'
import { useLocalStorage } from '@vueuse/core'
import { buildDropeeStorageKey } from '@/config/config'
import { ongoingTap } from '@/composables/game/ongoing-tap'
import type { PlayerUpgrade } from '@/types/shared/player-config'

export const useDailyCombo = () => {
  const { nowDateHourTimestamp, nowDateMinuteTimestamp } = useNow()
  const { config } = useConfig()
  const { player, playerStatsAfterDailyCombo, resetPlayerStatsForDailyCombo } = usePlayer()

  const dailyComboDate = computed(() => getNextOccurrence(config.value.game.challenges.dailyCombo.endHour, nowDateHourTimestamp.value).toISODate())
  const dailyComboDuration = computed(() => getNextOccurrenceDiffFromNow(config.value.game.challenges.dailyCombo.endHour, nowDateMinuteTimestamp.value))
  const dailyComboTime = computed(() => dailyComboDuration.value.toFormat('hh:mm'))
  const dailyComboIsAlmostOver = computed(() => dailyComboDuration.value.shiftTo('hours').hours < 1)

  const foundCombo = computed(() => {
    if (player.value.challenges.dailyCombo?.currentDate !== dailyComboDate.value) return []
    return player.value.challenges.dailyCombo?.foundCombo ?? []
  })
  const isDailyComboDone = computed(() => foundCombo.value.length === 3)

  const foundComboCards = computed<PlayerUpgrade[]>((oldValue) => {
    const newValue = foundCombo.value.map(upgradeId => config.value.upgrades.find(u => u.id === upgradeId)).filter(u => !!u)
    if (!oldValue) return newValue

    const oldValueHash = oldValue?.map(c => c.id).join(',')
    const newValueHash = newValue.map(c => c.id).join(',')

    return oldValueHash === newValueHash ? oldValue : newValue
  })

  return {
    foundCombo,
    foundComboCards,
    dailyComboTime,
    dailyComboIsAlmostOver,
    isDailyComboDone,
    dailyComboRewardAmount: computed(() => {
      return player.value.challenges.dailyCombo?.lastReward ?? config.value.game.challenges.dailyCombo.reward
    }),
    isComboCardDone: function (comboNumber: number) {
      return foundCombo.value.length >= comboNumber
    },
    playerStatsAfterDailyCombo,
    resetPlayerStatsForDailyCombo,
  }
}

export const useDailyCipher = () => {
  const { nowDateMinuteTimestamp } = useNow()
  const { config } = useConfig()
  const { player } = usePlayer()

  const dailyCipherDuration = computed(() => getNextOccurrenceDiffFromNow(config.value.game.challenges.dailyCipher.endHour, nowDateMinuteTimestamp.value))
  const dailyCipherTime = computed(() => dailyCipherDuration.value.toFormat('hh:mm'))
  const dailyCipherIsAlmostOver = computed(() => dailyCipherDuration.value.shiftTo('hours').hours < 1)

  const isDailyCipherDone = computed(() => {
    const lastCipher = player.value.challenges.dailyCipher?.lastDone
    if (!lastCipher) {
      return false
    }
    const today = getNextOccurrence(config.value.game.challenges.dailyCipher.endHour).minus({ second: 1 }).startOf('day')
    const lastCheckinParts = lastCipher.split('-')
    const lastCheckin = DateTime.utc().startOf('day').set({
      year: Number(lastCheckinParts[0]),
      month: Number(lastCheckinParts[1]),
      day: Number(lastCheckinParts[2]),
    })

    const daysDiff = today.diff(lastCheckin).as('days')
    return daysDiff <= 0
  })

  return {
    dailyCipherTime,
    dailyCipherIsAlmostOver,
    isDailyCipherDone,
  }
}

export const useDailyReward = () => {
  const { player, hasCheckedInToday } = usePlayer()
  const { playerProfit } = usePlayerProfit()
  const { config } = useConfig()

  function getDayPoints(dayIndex: number) {
    const dailyCheckinConfig = config.value.game.tasks.dailyCheckin
    if (dayIndex < dailyCheckinConfig.rewardsV2.length) {
      return dailyCheckinConfig.rewardsV2[Math.min(dayIndex, dailyCheckinConfig.rewardsV2.length - 1)].coins
    }
    return Math.max(dailyCheckinConfig.rewardsV2[dailyCheckinConfig.rewardsV2.length - 1].coins, dailyCheckinConfig.rewardAfterLastDay.hourProfit * playerProfit.value)
  }

  function getDaySpins(dayIndex: number) {
    const dailyRewards = config.value.game.tasks.dailyCheckin.rewardsV2
    if (dayIndex >= dailyRewards.length) {
      return 0
    }
    return dailyRewards[Math.min(dayIndex, dailyRewards.length - 1)].spins ?? 0

  }

  function isBigGoldenDay(dayIndex: number) {
    return isGoldenDay(dayIndex) && dayIndex !== 1
  }

  function isGoldenDay(dayIndex: number) {
    return getDaySpins(dayIndex) > 0
  }

  function isExtraGoldenDay(dayIndex: number) {
    return isGoldenDay(dayIndex) && getDayPoints(dayIndex) > 0
  }

  function getTodayPrize() {
    const consecutiveDays = player.value.tasks.dailyCheckin.consecutiveDays
    return getDayPoints(hasCheckedInToday.value ? consecutiveDays - 1 : consecutiveDays)
  }

  return {
    getDayPoints,
    getDaySpins,
    isGoldenDay,
    getTodayPrize,
    isExtraGoldenDay,
    isBigGoldenDay,
  }
}

export const useDailyQuestion = () => {
  const { config } = useConfig()
  const { player, updatePlayer } = usePlayer()
  const { nowDateTimestamp } = useNow()

  const reward = computed(() => config.value.game.challenges.dailyQuestion.reward)

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimed = computed(() => {
    if (!player.value.challenges.dailyQuestion?.lastDone) {
      return false
    }
    return player.value.challenges.dailyQuestion?.lastDone === todayDate.value
  })
  const answerSuccess = ref(claimed.value)

  async function getQuestion() {
    return (await api.getDailyQuestion()).question
  }

  async function sendAnswer(answer: string) {
    const { success, playerStats } = await api.answerDailyQuestion(answer)
    answerSuccess.value = success
    updatePlayer(playerStats)
  }

  return {
    reward,
    claimed,
    getQuestion,
    answerSuccess,
    sendAnswer,
  }
}

export const useAllDailyCheck = () => {
  const { hasCheckedInToday } = usePlayer()
  const { isDailyComboDone } = useDailyCombo()

  return {
    allDailyDone: computed(() => {
      return hasCheckedInToday.value && isDailyComboDone.value
    }),
  }
}

export const useDailyCrackTheEgg = () => {
  const { player } = usePlayer()
  const { nowDateIso: todayDate } = useNow()

  const claimedDailyCrackTheEgg = computed(() => {
    return Boolean(player.value.tasks.dailyTasks?.date === todayDate.value && player.value.tasks.dailyTasks?.claimed[DAILY_TASK_ID.TON_DAILY_CHECKIN_LEGACY])
  })
  const lastEggCrack = useLocalStorage(buildDropeeStorageKey('lingoRaffle.lastCrack'), '')
  const hasCrackedToday = computed(() => false)

  return {
    claimedDailyCrackTheEgg,
    lastEggCrack,
    hasCrackedToday,
  }
}

export const useDailyTx = () => {
  const { player } = usePlayer()
  const { nowDateIso: todayDate, now } = useNow()

  const claimedDailyTx = computed(() => {
    return Boolean(player.value.tasks.dailyTasks?.date === todayDate.value && player.value.tasks.dailyTasks?.claimed[DAILY_TASK_ID.TON_DAILY_CHECKIN])
  })
  const lastTxTimestamp = useLocalStorage(buildDropeeStorageKey('lastTxTimestamp'), 0)
  const hasDoneTxToday = computed(() => lastTxTimestamp.value > 0 && DateTime.fromSeconds(lastTxTimestamp.value, { zone: 'utc' }).startOf('day').toISODate() === todayDate.value)
  const hasRecentlyDoneTx = computed(() => now.value - lastTxTimestamp.value <= 60)

  const dailyTxModalTimestamp = useLocalStorage<string>(buildDropeeStorageKey('dailyTxModalTimestamp'), '')

  const canShowDailyTxModal = computed(() => {
    if (dailyTxModalTimestamp.value) {
      return !isTodayDate(DateTime.fromISO(dailyTxModalTimestamp.value))
    }
    return true
  })

  function setDailyTxModalTimestamp(value: string | null = null) {
    dailyTxModalTimestamp.value = value ?? todayDate.value
  }

  return {
    claimedDailyTx,
    hasDoneTxToday,
    hasRecentlyDoneTx,
    lastTxTimestamp,
    setDailyTxModalTimestamp,
    canShowDailyTxModal,
  }
}

export const useDailyRadarTx = () => {
  const { player } = usePlayer()
  const { nowDateIso: todayDate, now } = useNow()

  const claimedDailyRadar = computed(() => {
    return Boolean(player.value.tasks.dailyTasks?.date === todayDate.value && player.value.tasks.dailyTasks?.claimed[DAILY_TASK_ID.DAPP_RADAR_TTM])
  })
  const lastRadarTimestamp = useLocalStorage(buildDropeeStorageKey('lastRadarTxTimestamp'), 0)
  const hasDoneRadarTxToday = computed(() => lastRadarTimestamp.value > 0 && DateTime.fromSeconds(lastRadarTimestamp.value, { zone: 'utc' }).startOf('day').toISODate() === todayDate.value)
  const hasRecentlyDoneRadarTx = computed(() => now.value - lastRadarTimestamp.value <= 60)

  const dailyRadarTxModalTimestamp = useLocalStorage<string>(buildDropeeStorageKey('dailyRadarTxModalTimestamp'), '')

  const canShowDailyRadarTxModal = computed(() => {
    if (dailyRadarTxModalTimestamp.value) {
      return !isTodayDate(DateTime.fromISO(dailyRadarTxModalTimestamp.value))
    }
    return true
  })

  function setDailyRadarTxModalTimestamp(value: string | null = null) {
    dailyRadarTxModalTimestamp.value = value ?? todayDate.value
  }

  return {
    claimedDailyRadar,
    hasDoneRadarTxToday,
    hasRecentlyDoneRadarTx,
    lastRadarTimestamp,
    setDailyRadarTxModalTimestamp,
    canShowDailyRadarTxModal,
  }
}

export const useDailyTap = () => {
  const { player } = usePlayer()
  const { config } = useConfig()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimedDailyTap = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.tap) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.tap
  })
  const maxTapCount = computed(() => config.value.game.tasks.dailyTasks.tap.required)
  const dailyTapCount = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return Math.min(maxTapCount.value, ongoingTap.value.count)
    return Math.min(player.value.activities[DAILY_TASK_ID.TAP] + ongoingTap.value.count, maxTapCount.value)
  })

  return {
    claimedDailyTap,
    dailyTapCount,
    maxTapCount,
  }
}

export const useDailyBuyStars = () => {
  const { player } = usePlayer()
  const { nowDateTimestamp } = useNow()
  const { config } = useConfig()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimedDailyBuyStars = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.buyStars) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.buyStars
  })
  const maxStarsCount = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.BUY_STARS].required)
  const dailyStarsDeposit = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return 0
    return player.value.activities[DAILY_TASK_ID.BUY_STARS]
  })

  return {
    claimedDailyBuyStars,
    dailyStarsDeposit,
    maxStarsCount,
  }
}

export const useDailyPurchaseTask = () => {
  const { player } = usePlayer()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimedDailyPurchaseTask = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.dailyPurchase) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.dailyPurchase
  })
  const maxDailyPurchaseCount = computed(() => 1)
  const didDailyTaskPurchase = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return false
    return player.value.activities[DAILY_TASK_ID.DAILY_PURCHASE]
  })

  return {
    claimedDailyPurchaseTask,
    maxDailyPurchaseCount,
    didDailyTaskPurchase,
  }
}

export const useDailyImprove = () => {
  const { player } = usePlayer()
  const { config } = useConfig()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimedDailyImprove = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.upgrade) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.upgrade
  })
  const maxImproveCount = computed(() => config.value.game.tasks.dailyTasks.upgrade.required)
  const dailyImproveCount = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return 0
    return Math.min(player.value.activities.upgrade, maxImproveCount.value)
  })

  return {
    claimedDailyImprove,
    dailyImproveCount,
    maxImproveCount,
  }
}

export const useDailyPoke = () => {
  const { player } = usePlayer()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())
  const claimedDailyPoke = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.poke) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.poke
  })
  const dailyPokeCount = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return 0
    return player.value.activities.poke ? 1 : 0
  })

  return {
    claimedDailyPoke,
    dailyPokeCount,
  }
}

export const useDailySpin = () => {
  const { player } = usePlayer()
  const { config } = useConfig()
  const { nowDateTimestamp } = useNow()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())

  const reward = computed(() => config.value.game.tasks.dailyTasks[DAILY_TASK_ID.SPIN_THE_WHEEL].reward)

  const claimed = computed(() => {
    if (!player.value.tasks.dailyTasks?.claimed.spinTheWheel) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks?.claimed.spinTheWheel
  })

  const dailySpinCount = computed(() => {
    if (!player.value.activities || player.value.activities.date !== todayDate.value) return 0
    return player.value.activities[DAILY_TASK_ID.SPIN_THE_WHEEL] ? 1 : 0
  })

  return {
    reward,
    claimed,
    dailySpinCount,
  }
}

export const useDailyAllDone = () => {
  const { player, hasCheckedInToday } = usePlayer()
  const { nowDateTimestamp } = useNow()
  const { claimedDailyTap } = useDailyTap()
  const { claimedDailyImprove } = useDailyImprove()
  const { claimedDailyPoke } = useDailyPoke()
  const { claimed: claimedDailyQuestion } = useDailyQuestion()
  const { claimed: claimedSpin } = useDailySpin()
  const { claimed: claimedWatchAd } = useAd()

  const todayDate = computed(() => thisOccurrence(0, nowDateTimestamp.value).toISODate())

  const claimedDailyAllDone = computed(() => {
    if (!player.value.tasks?.dailyTasks?.claimed?.allDone) {
      return false
    }
    return player.value.tasks.dailyTasks.date === todayDate.value && player.value.tasks.dailyTasks.claimed.allDone
  })
  const dailyAllDoneCount = computed(() => {
    if ((!player.value.activities || player.value.activities.date !== todayDate.value) && !hasCheckedInToday.value) return 0
    let doneItems = 0
    const claims = [claimedDailyTap.value, claimedDailyImprove.value, claimedDailyPoke.value, claimedDailyQuestion.value, hasCheckedInToday.value, claimedSpin.value, claimedWatchAd.value]
    claims.forEach(claimed => {
      if (claimed) doneItems++
    })
    return doneItems
  })
  const maxAllDoneCount = computed(() => 7)

  return {
    claimedDailyAllDone,
    dailyAllDoneCount,
    maxAllDoneCount,
  }
}

export const useStory = () => {
  const { player, claimTask } = usePlayer()
  const { config } = useConfig()

  const claimedStory = computed(() => !!player.value.tasks.dailyTasks?.claimed.shareToStory)
  const storyReward = computed(() => config.value.game.tasks.dailyTasks?.[DAILY_TASK_ID.SHARE_TO_STORY].reward ?? 0)

  async function claimStoryTask() {
    await claimTask(DAILY_TASK_ID.SHARE_TO_STORY)
  }

  return {
    claimedStory,
    storyReward,
    claimStoryTask,
  }
}
